import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import Header from './Header.tsx';
import StageDetailsSidebar from './StageDetailsSidebar.tsx';
import TreatmentModal from './TreatmentModal.tsx';
import useTheme from '../hooks/useTheme.ts';
import styles from './Fleet.module.css';

// PadTrack Completions Tab
// Features:
// - Pad/Well header selection (from API)
// - 3D well schematic visualization (from directional survey)
// - Stage-by-stage completion design details
// - Treatment data graph with Seismos integration

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// ============================================
// TYPE DEFINITIONS
// ============================================

interface Pad {
  id: number;
  name: string;
  fleet: number;
  jobNumber: string;
  wellCount: number;
  stageCount: number;
  status: string;
  startTime: string;
  endTime?: string;
}

interface WellCasing {
  startDepth: number;
  endDepth: number;
  insideDiameter: number;
  outsideDiameter: number;
  rating: string;
  weight: number;
}

interface Well {
  id: number;
  name: string;
  padName: string;
  api: string;
  stageCount: number;
  status: string;
  lateralLength: number;
  startTime: string;
  endTime?: string;
  tvd: number;
  td: number;
  curveStart: number;
  curveEnd: number;
  casing: WellCasing;
  azimuth: number;
}

interface Stage {
  id: string;
  stageNumber: number;
  status: string;
  lbsPerFoot: string;
  fluidPerFoot: string;
  chemsPerGal: string;
  clusters: number;
  shotsPerCluster: number;
  clusterSpacing: string;
  perfInterval: number;
  uniformityIndex: string;
  startDepth: number;
  endDepth: number;
}

interface PerforationStage {
  stageNumber: number;
  topPerf: number;
  bottomPerf: number;
  numberOfPerfs: number;
  perfSize: number;
  perfLength: number;
  clusters: number;
  shotsPerCluster: number;
  clusterSpacing: number;
  status: 'COMPLETED' | 'ACTIVE' | 'PLANNED';
}

interface SurveyPoint3D {
  md: number;
  tvd: number;
  east: number;
  north: number;
  inc: number;
  azi: number;
}

interface WellStructure {
  id: number;
  name: string;
  padName: string;
  api: string;
  status: 'COMPLETED' | 'ACTIVE' | 'PLANNED';
  startTime: string;
  endTime?: string;
  tvd: number;
  td: number;
  curveStart: number;
  curveEnd: number;
  lateralLength: number;
  casing: WellCasing;
  azimuth: number;
  perforationStages: PerforationStage[];
  survey: SurveyPoint3D[];
}

// ============================================
// HELPERS
// ============================================

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

const formatTimestamp = (ts: number | null | undefined): string => {
  if (!ts) return '';
  const ms = ts < 10000000000 ? ts * 1000 : ts;
  const d = new Date(ms);
  if (isNaN(d.getTime())) return '';
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = String(d.getFullYear()).slice(2);
  const hours = d.getHours();
  const mins = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const h12 = hours % 12 || 12;
  return `${month}/${day}/${year} ${h12}:${mins}${ampm}`;
};

const deriveWellStatus = (stages: any[]): 'COMPLETED' | 'ACTIVE' | 'PLANNED' => {
  if (!stages || stages.length === 0) return 'PLANNED';
  const allCompleted = stages.every((s: any) => s.end_ts);
  const hasActive = stages.some((s: any) => s.start_ts && !s.end_ts);
  const hasCompleted = stages.some((s: any) => s.end_ts);
  if (allCompleted) return 'COMPLETED';
  if (hasActive || hasCompleted) return 'ACTIVE';
  return 'PLANNED';
};

const deriveStageStatus = (stage: any): string => {
  if (stage.end_ts) return 'COMPLETED';
  if (stage.start_ts) return 'ACTIVE';
  return 'PLANNED';
};

const computeAzimuth = (survey: any[], wellIndex: number, totalWells: number): number => {
  if (survey && survey.length >= 2) {
    const last = survey[survey.length - 1];
    const first = survey[0];
    const dN = (last.grid_north_ft || 0) - (first.grid_north_ft || 0);
    const dE = (last.grid_east_ft || 0) - (first.grid_east_ft || 0);
    if (Math.abs(dN) > 1 || Math.abs(dE) > 1) {
      return (Math.atan2(dE, dN) * 180 / Math.PI + 360) % 360;
    }
  }
  const spread = 60;
  const startAz = 360 - spread / 2;
  return totalWells > 1
    ? (startAz + (wellIndex / (totalWells - 1)) * spread) % 360
    : 0;
};

// ============================================
// COMPONENT
// ============================================

const STATUS_FILTERS = ['ALL', 'ACTIVE', 'PLANNED', 'COMPLETED'] as const;
type StatusFilter = typeof STATUS_FILTERS[number];

// Single source of truth for status color — same values as PadData.tsx.
// `solid` is the full-strength hex used for dots, chips and the 3D scene;
// bg/text/border are the pill treatment.
const STATUS_COLORS: Record<string, { bg: string; text: string; border: string; solid: string; hex: number }> = {
  ACTIVE:    { bg: 'rgba(34,197,94,0.15)',  text: '#16a34a', border: 'rgba(34,197,94,0.4)',  solid: '#22C55E', hex: 0x22C55E },
  COMPLETED: { bg: 'rgba(99,102,241,0.15)', text: '#6366f1', border: 'rgba(99,102,241,0.4)', solid: '#6366F1', hex: 0x6366F1 },
  PLANNED:   { bg: 'rgba(249,196,48,0.15)', text: '#b45309', border: 'rgba(249,196,48,0.4)', solid: '#F9C430', hex: 0xF9C430 },
};

const statusColor = (status: string) =>
  STATUS_COLORS[status] ?? { bg: 'rgba(156,163,175,0.15)', text: '#9CA3AF', border: 'rgba(156,163,175,0.4)', solid: '#666666', hex: 0x666666 };

const FILTER_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  ALL:       { bg: 'rgba(140,197,64,0.15)', text: '#8CC540', border: 'rgba(140,197,64,0.5)' },
  ACTIVE:    STATUS_COLORS.ACTIVE,
  PLANNED:   STATUS_COLORS.PLANNED,
  COMPLETED: STATUS_COLORS.COMPLETED,
};

// Lifecycle order, not alphabetical — sorting by status puts what's running now on top.
const STATUS_RANK: Record<string, number> = { ACTIVE: 0, PLANNED: 1, COMPLETED: 2 };

const PAD_SORT_FIELDS = [
  { label: 'Fleet', field: 'fleet' },
  { label: 'Name', field: 'name' },
  { label: 'Status', field: 'status' },
  { label: 'Wells', field: 'wellCount' },
  { label: 'Stages', field: 'stageCount' },
];

const WELL_SORT_FIELDS = [
  { label: 'Name', field: 'name' },
  { label: 'Status', field: 'status' },
  { label: 'Pad', field: 'padName' },
  { label: 'Stages', field: 'stageCount' },
];

const compareBy = (a: any, b: any, field: string, dir: 'asc' | 'desc'): number => {
  let av = a?.[field];
  let bv = b?.[field];

  if (field === 'status') {
    av = STATUS_RANK[av] ?? 99;
    bv = STATUS_RANK[bv] ?? 99;
  }

  // Missing values always sink to the bottom, whichever direction is active.
  if (av == null || av === '') return 1;
  if (bv == null || bv === '') return -1;

  const cmp =
    typeof av === 'string' && typeof bv === 'string'
      ? av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' })
      : Number(av) - Number(bv);

  return dir === 'asc' ? cmp : -cmp;
};

const CompletionsTab = () => {
  const { theme } = useTheme();
  const [selectedView, setSelectedView] = useState<'pads' | 'wells'>('pads');
  const [selectedPad, setSelectedPad] = useState<Pad | null>(null);
  const [selectedWell, setSelectedWell] = useState<Well | null>(null);
  const [selectedStage, setSelectedStage] = useState<Stage | null>(null);
  const [hoveredStage, setHoveredStage] = useState<string | null>(null);
  const [hovered3DStage, setHovered3DStage] = useState<{ wellName: string; stageNumber: number; status: string; x: number; y: number } | null>(null);
  const [selectedStageWell, setSelectedStageWell] = useState<Well | null>(null);
  const [showTreatmentModal, setShowTreatmentModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ACTIVE');
  const [sortField, setSortField] = useState<string>('fleet');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // ── API-loaded state ──
  const [pads, setPads] = useState<Pad[]>([]);
  const [wellsWithStructure, setWellsWithStructure] = useState<WellStructure[]>([]);
  const [stagesMap, setStagesMap] = useState<Record<number, Stage[]>>({});
  const [loadingPads, setLoadingPads] = useState(true);
  const [loadingCompletions, setLoadingCompletions] = useState(false);

  // Refs for 3D
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const animationRef = useRef<number | null>(null);
  const stageMeshesRef = useRef<THREE.Mesh[]>([]);
  const raycasterRef = useRef<THREE.Raycaster>(new THREE.Raycaster());
  const mouseRef = useRef<THREE.Vector2>(new THREE.Vector2());

  // ============================================
  // API DATA FETCHING
  // ============================================

  // Fetch pads on mount
  useEffect(() => {
    const fetchPads = async () => {
      setLoadingPads(true);
      try {
        const headers = getAuthHeaders();
        const res = await fetch(`${backendUrl}/pads`, { headers });
        if (!res.ok) throw new Error('Failed to fetch pads');
        const raw = await res.json();
        const data = Array.isArray(raw) ? raw : (raw.data || []);
        setPads(data.map((p: any) => ({
          id: p.id,
          name: p.pad_name || p.name || `Pad ${p.id}`,
          fleet: p.fleet || 0,
          jobNumber: String(p.job_number || ''),
          wellCount: p.well_count || 0,
          stageCount: p.stage_count || 0,
          status: (p.status || 'PLANNED').toUpperCase(),
          startTime: formatTimestamp(p.start_ts),
          endTime: formatTimestamp(p.end_ts),
        })));
      } catch (err: any) {
        console.error('Failed to fetch pads:', err);
      } finally {
        setLoadingPads(false);
      }
    };
    fetchPads();
  }, []);

  // Fetch completions when a pad is selected
  useEffect(() => {
    if (!selectedPad) {
      setWellsWithStructure([]);
      setStagesMap({});
      return;
    }

    const fetchCompletions = async () => {
      setLoadingCompletions(true);
      try {
        const headers = getAuthHeaders();

        // GET /pads/{id}/completions → [{well, survey, stages}]
        const res = await fetch(`${backendUrl}/pads/${selectedPad.id}/completions`, { headers });
        if (!res.ok) throw new Error('Failed to fetch completions');
        const completionsData: any[] = await res.json();

        // Also fetch casing strings for each well in parallel
        const casingPromises = completionsData.map((c: any) =>
          fetch(`${backendUrl}/wells/${c.well.id}/casing-strings`, { headers })
            .then(r => r.ok ? r.json() : [])
            .catch(() => [])
        );
        const allCasings = await Promise.all(casingPromises);
        const totalWells = completionsData.length;

        // Map to WellStructure[]
        const mappedWells: WellStructure[] = completionsData.map((c: any, idx: number) => {
          const w = c.well;
          const survey = c.survey || [];
          const apiStages = c.stages || [];
          const casings = allCasings[idx] || [];

          const status = deriveWellStatus(apiStages);
          const curveEnd = w.curve_end || w.td || 0;
          const td = w.td || 0;
          const lateralLength = Math.max(0, td - curveEnd);

          // Pick the primary casing
          const primaryCasing = casings.find((cs: any) => cs.pipe_type === 'casing') || casings[0];
          const casing: WellCasing = primaryCasing ? {
            startDepth: primaryCasing.top_depth || 0,
            endDepth: primaryCasing.bottom_depth || td,
            insideDiameter: primaryCasing.pipe_id || 4.778,
            outsideDiameter: primaryCasing.pipe_od || 5.5,
            rating: primaryCasing.grade || '',
            weight: primaryCasing.weight || 0,
          } : {
            startDepth: 0, endDepth: td,
            insideDiameter: 4.778, outsideDiameter: 5.5,
            rating: '', weight: 0,
          };

          const azimuth = computeAzimuth(survey, idx, totalWells);

          // Map stages → PerforationStage[] (one per stage, aggregating clusters)
          const stageSpacing = apiStages.length > 0 ? lateralLength / apiStages.length : 150;

          const perforationStages: PerforationStage[] = apiStages.map((s: any) => {
            const perfs = s.perforations || [];
            const clusterCount = perfs.length;
            const allTops = perfs.map((p: any) => p.top_depth_ft || 0).filter((v: number) => v > 0);
            const allBottoms = perfs.map((p: any) => p.bottom_depth_ft || 0).filter((v: number) => v > 0);

            const estTop = curveEnd + (apiStages.length - s.stage_number) * stageSpacing;
            const estBottom = estTop + stageSpacing;

            let topPerf = allTops.length > 0 ? Math.min(...allTops) : Math.round(estTop);
            let bottomPerf = allBottoms.length > 0 ? Math.max(...allBottoms) : Math.round(estBottom);

            if (Math.abs(bottomPerf - topPerf) < 1) {
              topPerf -= stageSpacing / 2;
              bottomPerf += stageSpacing / 2;
            }
            if (topPerf > bottomPerf) [topPerf, bottomPerf] = [bottomPerf, topPerf];

            return {
              stageNumber: s.stage_number,
              topPerf: Math.round(topPerf),
              bottomPerf: Math.round(bottomPerf),
              numberOfPerfs: perfs.reduce((sum: number, p: any) => sum + (p.holes || 0), 0),
              perfSize: perfs[0]?.diameter_in || 0.42,
              perfLength: Math.abs(bottomPerf - topPerf),
              clusters: clusterCount,
              shotsPerCluster: perfs[0]?.holes || 0,
              clusterSpacing: clusterCount > 1 ? Math.abs(bottomPerf - topPerf) / clusterCount : 0,
              status: deriveStageStatus(s) as 'COMPLETED' | 'ACTIVE' | 'PLANNED',
            };
          });

          // Map survey for 3D
          const surveyPoints: SurveyPoint3D[] = (survey || [])
            .map((s: any) => ({
              md: s.md_ft || 0,
              tvd: s.tvd_ft || 0,
              east: s.grid_east_ft || 0,
              north: s.grid_north_ft || 0,
              inc: s.inc_deg || 0,
              azi: s.azi_deg || 0,
            }))
            .sort((a: SurveyPoint3D, b: SurveyPoint3D) => a.md - b.md);

          return {
            id: w.id,
            name: w.well_name || `Well ${w.id}`,
            padName: selectedPad.name,
            api: w.api || '',
            status,
            startTime: formatTimestamp(w.start_ts),
            endTime: formatTimestamp(w.end_ts),
            tvd: w.tvd || 0,
            td,
            curveStart: w.curve_start || 0,
            curveEnd,
            lateralLength,
            azimuth,
            casing,
            perforationStages,
            survey: surveyPoints,
          };
        });

        setWellsWithStructure(mappedWells);

        // Build stagesMap for grid view
        const newStagesMap: Record<number, Stage[]> = {};
        completionsData.forEach((c: any) => {
          const w = c.well;
          const apiStages = c.stages || [];
          newStagesMap[w.id] = apiStages.map((s: any) => {
            const perfs = s.perforations || [];
            const clusterCount = perfs.length;
            const allTops = perfs.map((p: any) => p.top_depth_ft || 0);
            const allBottoms = perfs.map((p: any) => p.bottom_depth_ft || 0);
            const topPerf = allTops.length > 0 ? Math.min(...allTops) : 0;
            const bottomPerf = allBottoms.length > 0 ? Math.max(...allBottoms) : 0;
            const perfInterval = Math.abs(bottomPerf - topPerf);

            return {
              id: String(s.id),
              stageNumber: s.stage_number,
              status: deriveStageStatus(s),
              lbsPerFoot: '—',
              fluidPerFoot: '—',
              chemsPerGal: '—',
              clusters: clusterCount,
              shotsPerCluster: perfs[0]?.holes || 0,
              clusterSpacing: clusterCount > 1 ? (perfInterval / clusterCount).toFixed(1) : '0',
              perfInterval: Math.round(perfInterval),
              uniformityIndex: '—',
              startDepth: Math.round(topPerf),
              endDepth: Math.round(bottomPerf),
            };
          });
        });
        setStagesMap(newStagesMap);

      } catch (err: any) {
        console.error('Failed to fetch completions:', err);
      } finally {
        setLoadingCompletions(false);
      }
    };

    fetchCompletions();
  }, [selectedPad]);

  // ── Derived state ──
  const wells: Well[] = useMemo(() => wellsWithStructure.map(w => ({
    id: w.id,
    name: w.name,
    padName: w.padName,
    api: w.api,
    stageCount: w.perforationStages.length,
    status: w.status,
    lateralLength: w.lateralLength,
    startTime: w.startTime,
    endTime: w.endTime,
    tvd: w.tvd,
    td: w.td,
    curveStart: w.curveStart,
    curveEnd: w.curveEnd,
    casing: w.casing,
    azimuth: w.azimuth,
  })), [wellsWithStructure]);

  // ── Search + status filter + sort for the left list ──
  const statusCounts = useMemo(() => {
    const source: Array<{ status: string }> = selectedView === 'pads' ? pads : wells;
    return source.reduce<Record<string, number>>((acc, item) => {
      const st = item.status || 'PLANNED';
      acc[st] = (acc[st] || 0) + 1;
      return acc;
    }, {});
  }, [selectedView, pads, wells]);

  const visiblePads = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return pads
      .filter((pad) => {
        const matchSearch = !q || pad.name.toLowerCase().includes(q);
        const matchStatus = statusFilter === 'ALL' || (pad.status || 'PLANNED') === statusFilter;
        return matchSearch && matchStatus;
      })
      .sort((a, b) => compareBy(a, b, sortField, sortDirection));
  }, [pads, searchTerm, statusFilter, sortField, sortDirection]);

  const visibleWells = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return wells
      .filter((well) => {
        const matchSearch = !q || well.name.toLowerCase().includes(q);
        const matchStatus = statusFilter === 'ALL' || (well.status || 'PLANNED') === statusFilter;
        return matchSearch && matchStatus;
      })
      .sort((a, b) => compareBy(a, b, sortField, sortDirection));
  }, [wells, searchTerm, statusFilter, sortField, sortDirection]);

  const sortFields = selectedView === 'pads' ? PAD_SORT_FIELDS : WELL_SORT_FIELDS;
  const totalInView = selectedView === 'pads' ? pads.length : wells.length;
  const shownInView = selectedView === 'pads' ? visiblePads.length : visibleWells.length;


  const stages: Stage[] = selectedWell ? (stagesMap[selectedWell.id] || []) : [];

  // Placeholder for StageDetailsSidebar (will be fetched per-stage later)
  const treatmentData = useMemo(() => [] as any[], []);
  const seismosData = useMemo(() => ({ stageUI: 0, drops: [] as any[] }), []);

  // ============================================
  // 3D WELL VISUALIZATION — Built from directional survey data
  // ============================================

  useEffect(() => {
    if (!canvasRef.current || !selectedPad) return;
    if (loadingCompletions || wellsWithStructure.length === 0) return;

    // ── Scene setup ──
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1d1f);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(
      60,
      canvasRef.current.clientWidth / canvasRef.current.clientHeight,
      0.1,
      2000,
    );
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ canvas: canvasRef.current, antialias: true });
    renderer.setSize(canvasRef.current.clientWidth, canvasRef.current.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enableZoom = true;
    controls.enablePan = true;
    controls.enableRotate = true;
    controls.minDistance = 5;
    controls.maxDistance = 300;
    controls.maxPolarAngle = Math.PI;
    controls.minPolarAngle = 0;

    // Lighting
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const dl1 = new THREE.DirectionalLight(0xffffff, 0.8);
    dl1.position.set(20, 40, 20);
    scene.add(dl1);
    const dl2 = new THREE.DirectionalLight(0xffffff, 0.4);
    dl2.position.set(-20, 30, -20);
    scene.add(dl2);

    // Scale: 1 scene unit = 1000 ft
    const SCALE = 1 / 1000;
    const WELL_RADIUS = 0.12;

    // ── Survey interpolation helpers ──

    /** Interpolate 3D position at a given measured depth along a survey */
    const interpolateAtMD = (md: number, surveyPts: SurveyPoint3D[]): THREE.Vector3 => {
      if (surveyPts.length === 0) return new THREE.Vector3(0, 0, 0);
      if (md <= surveyPts[0].md) {
        const s = surveyPts[0];
        return new THREE.Vector3(s.east * SCALE, -s.tvd * SCALE, s.north * SCALE);
      }
      const last = surveyPts[surveyPts.length - 1];
      if (md >= last.md) {
        return new THREE.Vector3(last.east * SCALE, -last.tvd * SCALE, last.north * SCALE);
      }
      for (let i = 0; i < surveyPts.length - 1; i++) {
        const a = surveyPts[i];
        const b = surveyPts[i + 1];
        if (md >= a.md && md <= b.md) {
          const t = (b.md - a.md) > 0 ? (md - a.md) / (b.md - a.md) : 0;
          return new THREE.Vector3(
            (a.east + t * (b.east - a.east)) * SCALE,
            -(a.tvd + t * (b.tvd - a.tvd)) * SCALE,
            (a.north + t * (b.north - a.north)) * SCALE,
          );
        }
      }
      return new THREE.Vector3(last.east * SCALE, -last.tvd * SCALE, last.north * SCALE);
    };

    /** Get points between two MDs for smooth tube curves */
    const getPathBetweenMDs = (
      startMD: number, endMD: number, surveyPts: SurveyPoint3D[], numSeg: number = 12,
    ): THREE.Vector3[] => {
      const points: THREE.Vector3[] = [];
      for (let i = 0; i <= numSeg; i++) {
        const md = startMD + (i / numSeg) * (endMD - startMD);
        points.push(interpolateAtMD(md, surveyPts));
      }
      return points;
    };

    /** Build synthetic survey when no real survey exists */
    const buildFallbackSurvey = (well: WellStructure): SurveyPoint3D[] => {
      const pts: SurveyPoint3D[] = [];
      const azRad = (well.azimuth * Math.PI) / 180;
      const curveStart = well.curveStart || well.tvd * 0.95;
      const curveEnd = well.curveEnd || well.tvd;
      const td = well.td || curveEnd + 10000;

      // Surface
      pts.push({ md: 0, tvd: 0, east: 0, north: 0, inc: 0, azi: well.azimuth });

      // Vertical section
      const vertSteps = Math.max(2, Math.floor(curveStart / 500));
      for (let i = 1; i <= vertSteps; i++) {
        const md = (i / vertSteps) * curveStart;
        pts.push({ md, tvd: md, east: 0, north: 0, inc: 0, azi: well.azimuth });
      }

      // Build section (curve 90°)
      const curveLength = curveEnd - curveStart;
      const buildAngle = Math.PI / 2;
      const radius = curveLength / buildAngle;
      for (let i = 1; i <= 20; i++) {
        const t = i / 20;
        const angle = t * buildAngle;
        const tvd = curveStart + radius * Math.sin(angle);
        const horizDisp = radius * (1 - Math.cos(angle));
        const md = curveStart + t * curveLength;
        pts.push({
          md, tvd,
          east: Math.sin(azRad) * horizDisp,
          north: Math.cos(azRad) * horizDisp,
          inc: (angle * 180) / Math.PI,
          azi: well.azimuth,
        });
      }

      // Lateral section
      const lateralLen = td - curveEnd;
      const lastPt = pts[pts.length - 1];
      const latSteps = Math.max(2, Math.floor(lateralLen / 500));
      for (let i = 1; i <= latSteps; i++) {
        const t = i / latSteps;
        const dist = t * lateralLen;
        pts.push({
          md: curveEnd + dist,
          tvd: lastPt.tvd + dist * 0.01,
          east: lastPt.east + Math.sin(azRad) * dist,
          north: lastPt.north + Math.cos(azRad) * dist,
          inc: 90,
          azi: well.azimuth,
        });
      }
      return pts;
    };

    // ── Color palettes ──
    const stageColors: Record<string, number> = {
      COMPLETED: STATUS_COLORS.COMPLETED.hex, ACTIVE: STATUS_COLORS.ACTIVE.hex, PLANNED: 0x666666,
    };
    const wellColors: Record<string, number> = {
      COMPLETED: STATUS_COLORS.COMPLETED.hex, ACTIVE: STATUS_COLORS.ACTIVE.hex, PLANNED: 0x666666,
    };

    // ── Clear previous meshes ──
    stageMeshesRef.current = [];
    const allPoints: THREE.Vector3[] = [];

    // ══════════════════════════════════════════════
    // BUILD EACH WELL
    // ══════════════════════════════════════════════
    wellsWithStructure.forEach((well) => {
      const wellGroup = new THREE.Group();

      // Use real survey if ≥ 3 points with real MD data; else fallback
      const hasSurvey = well.survey && well.survey.length >= 3
        && well.survey.some(s => s.md > 0);
      const survey: SurveyPoint3D[] = hasSurvey ? well.survey : buildFallbackSurvey(well);
      const maxMD = survey[survey.length - 1]?.md || well.td || 0;

      // ── 1. Full wellbore backbone (muted tube) ──
      const fullPathPoints = survey.map(s =>
        new THREE.Vector3(s.east * SCALE, -s.tvd * SCALE, s.north * SCALE),
      );
      fullPathPoints.forEach(p => allPoints.push(p));

      if (fullPathPoints.length >= 2) {
        const backboneCurve = new THREE.CatmullRomCurve3(fullPathPoints);
        const backboneGeo = new THREE.TubeGeometry(
          backboneCurve, Math.max(20, fullPathPoints.length * 2),
          WELL_RADIUS * 0.75, 10, false,
        );
        // Light steel with a low self-glow so the pipe reads on the dark scene
        // even where no directional light hits it.
        const backboneMat = new THREE.MeshStandardMaterial({
          color: 0xB4C2CE,
          metalness: 0.25, roughness: 0.6,
          emissive: 0x5A6A78, emissiveIntensity: 0.45,
          transparent: false, opacity: 1.0,
        });
        wellGroup.add(new THREE.Mesh(backboneGeo, backboneMat));

        // Thin bright center line drawn over the tube — keeps the trajectory
        // legible when zoomed out and the tube is only a few pixels wide.
        const spineGeo = new THREE.BufferGeometry().setFromPoints(
          backboneCurve.getPoints(Math.max(60, fullPathPoints.length * 4)),
        );
        const spineMat = new THREE.LineBasicMaterial({
          color: 0xE6EDF3, transparent: true, opacity: 0.55,
        });
        wellGroup.add(new THREE.Line(spineGeo, spineMat));
      }

      // ── 2. Stage segments (one per stage — colored, clickable) ──
      well.perforationStages.forEach((stage) => {
        const startMD = stage.topPerf;
        const endMD = stage.bottomPerf;
        if (endMD <= startMD || startMD >= maxMD) return;

        const segPoints = getPathBetweenMDs(startMD, endMD, survey, 8);
        if (segPoints.length < 2) return;

        const stageCurve = new THREE.CatmullRomCurve3(segPoints);
        const stageGeo = new THREE.TubeGeometry(stageCurve, 8, WELL_RADIUS * 0.92, 10, false);
        const stageMat = new THREE.MeshStandardMaterial({
          color: stageColors[stage.status] || 0x666666,
          metalness: 0.3, roughness: 0.5,
          emissive: stage.status === 'ACTIVE' ? STATUS_COLORS.ACTIVE.hex : 0x000000,
          emissiveIntensity: stage.status === 'ACTIVE' ? 0.5 : 0,
        });

        const stageMesh = new THREE.Mesh(stageGeo, stageMat);
        stageMesh.userData = {
          wellId: well.id,
          stageNumber: stage.stageNumber,
          wellName: well.name,
          status: stage.status,
        };
        wellGroup.add(stageMesh);
        stageMeshesRef.current.push(stageMesh);

        // ── Cluster markers within the stage ──
        if (stage.clusters > 0) {
          const clusterColor = stageColors[stage.status] || 0x666666;
          const emissiveColor = stage.status === 'ACTIVE' ? STATUS_COLORS.ACTIVE.hex : 0x000000;
          const emissiveInt = stage.status === 'ACTIVE' ? 0.8 : 0;

          // Each cluster gets a thin disc whose thickness = 30% of the slot,
          // leaving 70% as visible gap — no overlap no matter how many clusters.
          const clusterSlotScene = (endMD - startMD) * SCALE / stage.clusters;
          const discRadius = WELL_RADIUS * 1.55;          // just wider than the tube
          const discHeight = Math.max(clusterSlotScene * 0.22, 0.003);

          const discMat = new THREE.MeshStandardMaterial({
            color: clusterColor,
            metalness: 0.3, roughness: 0.4,
            emissive: emissiveColor,
            emissiveIntensity: emissiveInt,
            transparent: stage.status === 'PLANNED',
            opacity: stage.status === 'PLANNED' ? 0.50 : 1.0,
          });

          for (let c = 0; c < stage.clusters; c++) {
            const clusterMD = startMD + ((c + 0.5) / stage.clusters) * (endMD - startMD);
            const clusterPos = interpolateAtMD(clusterMD, survey);

            // Compute tangent at this MD to orient disc perpendicular to wellbore
            const eps = Math.max((endMD - startMD) / stage.clusters * 0.1, 2);
            const pA = interpolateAtMD(clusterMD - eps, survey);
            const pB = interpolateAtMD(clusterMD + eps, survey);
            const tangent = pB.clone().sub(pA).normalize();

            // Flat cylinder (disc) — Y-axis is its axis, so align Y→tangent
            const discGeo = new THREE.CylinderGeometry(discRadius, discRadius, discHeight, 12);
            const disc = new THREE.Mesh(discGeo, discMat);
            disc.position.copy(clusterPos);
            disc.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), tangent);
            disc.userData = {
              wellId: well.id, stageNumber: stage.stageNumber,
              wellName: well.name, status: stage.status,
            };
            wellGroup.add(disc);
            stageMeshesRef.current.push(disc);
          }
        }
      });

      // ── 3. Surface marker ──
      const surfacePos = interpolateAtMD(0, survey);
      const headGeo = new THREE.CylinderGeometry(WELL_RADIUS * 2, WELL_RADIUS * 2, 0.3, 12);
      const headMat = new THREE.MeshStandardMaterial({
        color: wellColors[well.status] || 0x666666,
        metalness: 0.5, roughness: 0.4,
      });
      const head = new THREE.Mesh(headGeo, headMat);
      head.position.set(surfacePos.x, surfacePos.y + 0.15, surfacePos.z);
      wellGroup.add(head);

      // ── 4. Well label at toe ──
      const toePos = interpolateAtMD(maxMD, survey);
      const labelCanvas = document.createElement('canvas');
      const ctx = labelCanvas.getContext('2d');
      labelCanvas.width = 256;
      labelCanvas.height = 64;
      if (ctx) {
        ctx.fillStyle = 'rgba(26, 29, 31, 0.9)';
        ctx.roundRect(0, 0, 256, 64, 8);
        ctx.fill();
        ctx.fillStyle = statusColor(well.status).solid;
        ctx.font = 'bold 18px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(well.name, 128, 25);
        ctx.fillStyle = '#aaaaaa';
        ctx.font = '12px Arial';
        ctx.fillText(`TD: ${well.td.toLocaleString()} ft | ${well.perforationStages.length} stg`, 128, 48);
      }
      const labelTex = new THREE.CanvasTexture(labelCanvas);
      const labelMat = new THREE.SpriteMaterial({ map: labelTex });
      const label = new THREE.Sprite(labelMat);
      label.position.set(toePos.x, toePos.y + 0.5, toePos.z);
      label.scale.set(4, 1, 1);
      wellGroup.add(label);

      scene.add(wellGroup);
    });

    // ── Ground plane ──
    const groundGeo = new THREE.PlaneGeometry(120, 120);
    const groundMat = new THREE.MeshStandardMaterial({
      color: 0x2d4a2d, roughness: 0.9, metalness: 0.1, side: THREE.DoubleSide,
    });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = 0.01;
    scene.add(ground);

    // Grid
    const gridHelper = new THREE.GridHelper(80, 40, 0x444444, 0x333333);
    gridHelper.position.y = 0.02;
    scene.add(gridHelper);

    // Depth rings
    [5, 10, 15, 20, 25].forEach(depth => {
      const ringGeo = new THREE.RingGeometry(39, 40, 32);
      const ringMat = new THREE.MeshBasicMaterial({ color: 0x333333, transparent: true, opacity: 0.2, side: THREE.DoubleSide });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = -depth;
      scene.add(ring);
    });

    // Subsurface layers
    [
      { depth: 5, thickness: 5, color: 0x4a3d2d },
      { depth: 10, thickness: 5, color: 0x3d3d4a },
      { depth: 15, thickness: 10, color: 0x2d3d4a },
    ].forEach(layer => {
      const layerGeo = new THREE.BoxGeometry(70, layer.thickness, 70);
      const layerMat = new THREE.MeshStandardMaterial({
        color: layer.color, transparent: true, opacity: 0.15, roughness: 0.8,
      });
      const layerMesh = new THREE.Mesh(layerGeo, layerMat);
      layerMesh.position.y = -layer.depth - layer.thickness / 2;
      scene.add(layerMesh);
    });

    // ── Auto-frame camera ──
    if (allPoints.length > 0) {
      const box = new THREE.Box3();
      allPoints.forEach(p => box.expandByPoint(p));
      const center = new THREE.Vector3();
      box.getCenter(center);
      const size = new THREE.Vector3();
      box.getSize(size);
      const maxDim = Math.max(size.x, size.y, size.z, 10);
      controls.target.copy(center);
      camera.position.set(center.x + maxDim * 0.8, center.y + maxDim * 0.5, center.z + maxDim * 1.0);
      camera.lookAt(center);
      controls.update();
    } else {
      controls.target.set(0, -5, 0);
      camera.position.set(40, -25, 50);
    }

    // ── Animation loop ──
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // ── Resize ──
    const handleResize = () => {
      if (!canvasRef.current) return;
      const w = canvasRef.current.clientWidth;
      const h = canvasRef.current.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    // ── Click handler ──
    const handleCanvasClick = (event: MouseEvent) => {
      if (!canvasRef.current || stageMeshesRef.current.length === 0) return;
      const rect = canvasRef.current.getBoundingClientRect();
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycasterRef.current.setFromCamera(mouseRef.current, camera);
      const intersects = raycasterRef.current.intersectObjects(stageMeshesRef.current, false);
      if (intersects.length > 0) {
        const ud = intersects[0].object.userData;
        if (ud && ud.wellId && ud.stageNumber) {
          const clickedWell = wells.find(w => w.id === ud.wellId);
          if (clickedWell) {
            setSelectedStageWell(clickedWell);
            const clickedStages = stagesMap[clickedWell.id] || [];
            const clickedStage = clickedStages.find(s => s.stageNumber === ud.stageNumber);
            if (clickedStage) setSelectedStage(clickedStage);
          }
        }
      }
    };

    // ── Double-click: move orbit anchor to clicked point ──
    const handleCanvasDblClick = (event: MouseEvent) => {
      if (!canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycasterRef.current.setFromCamera(mouseRef.current, camera);
      const allMeshes: THREE.Object3D[] = [];
      scene.traverse(obj => { if ((obj as THREE.Mesh).isMesh) allMeshes.push(obj); });
      const intersects = raycasterRef.current.intersectObjects(allMeshes, false);
      if (intersects.length > 0) {
        // Shift both target and camera by the same delta so OrbitControls'
        // internal spherical radius (zoom distance) is never touched.
        const delta = intersects[0].point.clone().sub(controls.target);
        controls.target.add(delta);
        camera.position.add(delta);
        // controls.update() will re-derive its spherical from the new
        // camera→target vector, which has the same length as before.
        controls.update();
      }
    };

    // ── Hover handler ──
    const handleCanvasMouseMove = (event: MouseEvent) => {
      if (!canvasRef.current || stageMeshesRef.current.length === 0) return;
      const rect = canvasRef.current.getBoundingClientRect();
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycasterRef.current.setFromCamera(mouseRef.current, camera);
      const intersects = raycasterRef.current.intersectObjects(stageMeshesRef.current, false);
      if (intersects.length > 0) {
        const ud = intersects[0].object.userData;
        if (ud && ud.wellName && ud.stageNumber) {
          setHovered3DStage({
            wellName: ud.wellName,
            stageNumber: ud.stageNumber,
            status: ud.status,
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
          });
          canvasRef.current.style.cursor = 'pointer';
        }
      } else {
        setHovered3DStage(null);
        if (canvasRef.current) canvasRef.current.style.cursor = 'grab';
      }
    };

    const handleCanvasMouseLeave = () => {
      setHovered3DStage(null);
      if (canvasRef.current) canvasRef.current.style.cursor = 'grab';
    };

    canvasRef.current.addEventListener('click', handleCanvasClick);
    canvasRef.current.addEventListener('dblclick', handleCanvasDblClick);
    canvasRef.current.addEventListener('mousemove', handleCanvasMouseMove);
    canvasRef.current.addEventListener('mouseleave', handleCanvasMouseLeave);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', handleResize);
      if (canvasRef.current) {
        canvasRef.current.removeEventListener('click', handleCanvasClick);
        canvasRef.current.removeEventListener('dblclick', handleCanvasDblClick);
        canvasRef.current.removeEventListener('mousemove', handleCanvasMouseMove);
        canvasRef.current.removeEventListener('mouseleave', handleCanvasMouseLeave);
      }
      renderer.dispose();
    };
  }, [selectedPad, wellsWithStructure, stagesMap, loadingCompletions, wells]);

  // ============================================
  // UI COMPONENTS
  // ============================================

  const StatusBadge = ({ status }: { status: string }) => {
    const c = statusColor(status);
    return (
      <span style={{
        backgroundColor: c.bg, color: c.text,
        border: `1px solid ${c.border}`,
        padding: '3px 10px', borderRadius: '999px',
        fontSize: '11px', fontWeight: 600, letterSpacing: '0.02em',
        textTransform: 'capitalize', whiteSpace: 'nowrap',
      }}>
        {status.toLowerCase()}
      </span>
    );
  };

  const CompletionCard = ({ stage }: { stage: Stage }) => (
    <div style={{
      backgroundColor: '#303335',
      border: selectedStage?.id === stage.id ? '2px solid #8CC540' : '1px solid #5C646B',
      borderRadius: '8px',
      padding: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      transform: hoveredStage === stage.id ? 'scale(1.02)' : 'scale(1)',
    }}
    onClick={() => setSelectedStage(stage)}
    onMouseEnter={() => setHoveredStage(stage.id)}
    onMouseLeave={() => setHoveredStage(null)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ color: '#fff', fontWeight: 600 }}>Stage {stage.stageNumber}</span>
        <StatusBadge status={stage.status} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '12px', color: '#aaa' }}>
        <div><span style={{ color: '#8CC540' }}>{stage.lbsPerFoot}</span> lbs/ft</div>
        <div><span style={{ color: '#3E76FE' }}>{stage.fluidPerFoot}</span> bbl/ft</div>
        <div><span style={{ color: '#cf2727' }}>{stage.clusters}</span> clusters</div>
        <div><span style={{ color: '#f5a623' }}>{stage.shotsPerCluster}</span> shots/cluster</div>
      </div>
    </div>
  );

  const TreatmentChart = () => {
    const width = 800;
    const height = 200;
    return (
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} style={{ backgroundColor: '#1a1d1f', borderRadius: '8px' }}>
        <text x={width / 2} y={height / 2} textAnchor="middle" fill="#666" fontSize="14">
          Select a stage to view treatment data
        </text>
      </svg>
    );
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <section className={styles.page} data-theme={theme}>
      <Header />

      <div style={{ display: 'flex', height: 'calc(100vh - 60px)', overflow: 'hidden' }}>
        {/* Left Sidebar - Pad/Well List */}
        <div style={{ 
          width: '300px',
          flexShrink: 0,
          minWidth: 0,
          boxSizing: 'border-box',
          borderRight: '1px solid #5C646B', 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: '#232629'
        }}>
          <div style={{ padding: '16px', borderBottom: '1px solid #5C646B' }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#8CC540', fontSize: '16px' }}>Completions</h3>
            <div style={{ display: 'flex', gap: '4px', backgroundColor: '#1a1d1f', borderRadius: '8px', padding: '3px' }}>
              <button 
                onClick={() => { setSelectedView('pads'); setSearchTerm(''); setSortField('fleet'); setSortDirection('asc'); }}
                style={{
                  flex: 1, padding: '10px', border: 'none', borderRadius: '6px',
                  backgroundColor: selectedView === 'pads' ? '#8CC540' : '#1a1d1f',
                  color: selectedView === 'pads' ? '#000' : '#aaa',
                  cursor: 'pointer', fontWeight: 600, fontSize: '13px'
                }}
              >
                📊 Pads
              </button>
              <button 
                onClick={() => { setSelectedView('wells'); setSearchTerm(''); setSortField('name'); setSortDirection('asc'); }}
                style={{
                  flex: 1, padding: '10px', border: 'none', borderRadius: '6px',
                  backgroundColor: selectedView === 'wells' ? '#8CC540' : '#1a1d1f',
                  color: selectedView === 'wells' ? '#000' : '#aaa',
                  cursor: 'pointer', fontWeight: 600, fontSize: '13px'
                }}
              >
                🛢️ Wells
              </button>
            </div>
          </div>

          {/* Search */}
          <div style={{ padding: '12px 14px 8px 14px' }}>
            <input 
              type="text" 
              placeholder={`Search ${selectedView === 'pads' ? 'pads' : 'wells'}...`}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '8px 11px',
                backgroundColor: '#1a1d1f', border: '1px solid #5C646B',
                borderRadius: '6px', color: '#fff', fontSize: '13px'
              }}
            />
          </div>

          {/* Status filter */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', padding: '0 14px 8px 14px' }}>
            {STATUS_FILTERS.map(s => {
              const isActive = statusFilter === s;
              const c = FILTER_COLORS[s];
              const count = s === 'ALL' ? totalInView : (statusCounts[s] || 0);
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  style={{
                    padding: '3px 8px',
                    borderRadius: '999px',
                    fontSize: '11px',
                    fontWeight: 600,
                    lineHeight: 1.5,
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                    border: isActive ? `1px solid ${c.border}` : '1px solid #3A3F44',
                    backgroundColor: isActive ? c.bg : 'transparent',
                    color: isActive ? c.text : '#8b9299',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {s.toLowerCase()} <span style={{ opacity: 0.55, fontWeight: 500 }}>{count}</span>
                </button>
              );
            })}
          </div>

          {/* Sort */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '0 14px 10px 14px' }}>
            <select
              value={sortField}
              onChange={e => setSortField(e.target.value)}
              style={{
                flex: 1, minWidth: 0, padding: '5px 8px',
                backgroundColor: 'transparent', border: '1px solid #3A3F44',
                borderRadius: '6px', color: '#aaa', fontSize: '11px', cursor: 'pointer',
              }}
            >
              {sortFields.map(f => (
                <option key={f.field} value={f.field} style={{ backgroundColor: '#1a1d1f' }}>Sort by {f.label}</option>
              ))}
            </select>
            <button
              onClick={() => setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'))}
              title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
              style={{
                padding: '5px 9px', flexShrink: 0,
                backgroundColor: 'transparent', border: '1px solid #3A3F44',
                borderRadius: '6px', color: '#8CC540', fontSize: '10px', cursor: 'pointer',
              }}
            >
              {sortDirection === 'asc' ? '▲' : '▼'}
            </button>
          </div>

          {/* List */}
          <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', overflowX: 'hidden', padding: '0 10px 10px 10px' }}>
            {selectedView === 'pads' ? (
              loadingPads ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#666' }}>Loading pads...</div>
              ) : pads.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#666' }}>No pads found</div>
              ) : visiblePads.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#666', fontSize: '13px' }}>No pads match your filters</div>
              ) :
              visiblePads.map(pad => (
                <div 
                  key={pad.id}
                  onClick={() => { setSelectedPad(pad); setSelectedWell(null); setSelectedStage(null); }}
                  onMouseEnter={e => { if (selectedPad?.id !== pad.id) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'; }}
                  onMouseLeave={e => { if (selectedPad?.id !== pad.id) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  style={{
                    padding: '10px 12px', margin: '2px 0', minWidth: 0,
                    backgroundColor: selectedPad?.id === pad.id ? 'rgba(140, 197, 64, 0.1)' : 'transparent',
                    borderLeft: selectedPad?.id === pad.id ? '2px solid #8CC540' : '2px solid transparent',
                    borderRadius: '6px', cursor: 'pointer', transition: 'background-color 0.15s ease'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '5px' }}>
                    <span
                      title={pad.name}
                      style={{
                        flex: 1, minWidth: 0, fontWeight: 600, color: '#fff', fontSize: '13px', lineHeight: 1.35,
                        overflow: 'hidden', overflowWrap: 'anywhere',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {pad.name}
                    </span>
                    <div style={{ flexShrink: 0 }}><StatusBadge status={pad.status} /></div>
                  </div>
                  <div style={{ fontSize: '11px', color: '#8b9299', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    Fleet {pad.fleet} <span style={{ opacity: 0.4 }}>·</span> {pad.wellCount} wells <span style={{ opacity: 0.4 }}>·</span> {pad.stageCount} stages
                  </div>
                </div>
              ))
            ) : wells.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#666', fontSize: '13px' }}>Select a pad to load its wells</div>
            ) : visibleWells.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#666', fontSize: '13px' }}>No wells match your filters</div>
            ) : (
              visibleWells.map(well => (
                <div 
                  key={well.id}
                  onClick={() => { setSelectedWell(well); setSelectedStage(null); }}
                  onMouseEnter={e => { if (selectedWell?.id !== well.id) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'; }}
                  onMouseLeave={e => { if (selectedWell?.id !== well.id) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  style={{
                    padding: '10px 12px', margin: '2px 0', minWidth: 0,
                    backgroundColor: selectedWell?.id === well.id ? 'rgba(140, 197, 64, 0.1)' : 'transparent',
                    borderLeft: selectedWell?.id === well.id ? '2px solid #8CC540' : '2px solid transparent',
                    borderRadius: '6px', cursor: 'pointer', transition: 'background-color 0.15s ease'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '5px' }}>
                    <span
                      title={well.name}
                      style={{
                        flex: 1, minWidth: 0, fontWeight: 600, color: '#fff', fontSize: '13px', lineHeight: 1.35,
                        overflow: 'hidden', overflowWrap: 'anywhere',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {well.name}
                    </span>
                    <div style={{ flexShrink: 0 }}><StatusBadge status={well.status} /></div>
                  </div>
                  <div style={{ fontSize: '11px', color: '#8b9299', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {well.padName} <span style={{ opacity: 0.4 }}>·</span> {well.stageCount} stages
                  </div>
                  <div style={{ fontSize: '10px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    API {well.api}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer count */}
          <div style={{
            padding: '8px 14px',
            borderTop: '1px solid #5C646B',
            fontSize: '11px',
            color: '#666',
            flexShrink: 0,
          }}>
            Showing {shownInView} of {totalInView} {selectedView}
          </div>
        </div>

        {/* Center Panel - 3D Visualization or Stage Grid */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#1a1d1f' }}>
          {selectedPad && !selectedWell && loadingCompletions ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#666' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '50%',
                border: '3px solid #303335', borderTopColor: '#8CC540',
                animation: 'spin 1s linear infinite',
              }} />
              <div style={{ marginTop: '16px', fontSize: '14px' }}>Loading completions data...</div>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : selectedPad && !selectedWell && wellsWithStructure.length === 0 && !loadingCompletions ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#666' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🛢️</div>
              <div style={{ fontSize: '16px' }}>No wells found for this pad</div>
              <div style={{ fontSize: '13px', marginTop: '8px', color: '#555' }}>Deploy wells from the Plan page first</div>
            </div>
          ) : selectedPad && !selectedWell ? (
            // 3D Well Schematic
            <div style={{ flex: 1, position: 'relative' }}>
              <div style={{ 
                position: 'absolute', top: '16px', left: '16px', zIndex: 10,
                backgroundColor: 'rgba(48, 51, 53, 0.9)',
                padding: '12px 16px', borderRadius: '8px', border: '1px solid #5C646B'
              }}>
                <h3 style={{ margin: '0 0 8px 0', color: '#8CC540', fontSize: '16px' }}>{selectedPad.name}</h3>
                <div style={{ fontSize: '12px', color: '#aaa' }}>
                  <div>Job Number: {selectedPad.jobNumber}</div>
                  <div>Wells: {selectedPad.wellCount} | Stages: {selectedPad.stageCount}</div>
                </div>
              </div>

              {/* Controls hint */}
              <div style={{
                position: 'absolute', top: '16px', right: '16px', zIndex: 10,
                backgroundColor: 'rgba(26, 29, 31, 0.75)',
                padding: '6px 10px', borderRadius: '6px', fontSize: '11px', color: '#666',
                pointerEvents: 'none',
              }}>
                Double-click to re-anchor orbit
              </div>
              
              {/* Legend with Well Information */}
              <div style={{ 
                position: 'absolute', bottom: '16px', left: '16px', zIndex: 10,
                backgroundColor: 'rgba(48, 51, 53, 0.95)',
                padding: '16px', borderRadius: '8px', border: '1px solid #5C646B',
                maxWidth: '420px', fontSize: '12px'
              }}>
                {/* Status Legend */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #5C646B' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: STATUS_COLORS.COMPLETED.solid, borderRadius: '2px' }}></div>
                    <span style={{ color: '#aaa' }}>Completed</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: STATUS_COLORS.ACTIVE.solid, borderRadius: '2px' }}></div>
                    <span style={{ color: '#aaa' }}>Active</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: STATUS_COLORS.ACTIVE.solid, borderRadius: '50%' }}></div>
                    <span style={{ color: '#aaa' }}>Active Cluster</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#666', borderRadius: '2px' }}></div>
                    <span style={{ color: '#aaa' }}>Planned</span>
                  </div>
                </div>
                
                {/* Well Information Table */}
                <div style={{ marginBottom: '8px', fontWeight: 600, color: '#8CC540' }}>Well Information</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #5C646B' }}>
                      <th style={{ padding: '4px 8px', textAlign: 'left', color: '#888' }}>Well</th>
                      <th style={{ padding: '4px 8px', textAlign: 'right', color: '#888' }}>TVD</th>
                      <th style={{ padding: '4px 8px', textAlign: 'right', color: '#888' }}>TD</th>
                      <th style={{ padding: '4px 8px', textAlign: 'right', color: '#888' }}>Lateral</th>
                      <th style={{ padding: '4px 8px', textAlign: 'center', color: '#888' }}>Stages</th>
                      <th style={{ padding: '4px 8px', textAlign: 'center', color: '#888' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wellsWithStructure.map((well, idx) => {
                      const completedStages = well.perforationStages.filter(s => s.status === 'COMPLETED').length;
                      const totalStages = well.perforationStages.length;
                      return (
                        <tr key={well.id} style={{ borderBottom: idx < wellsWithStructure.length - 1 ? '1px solid #444' : 'none' }}>
                          <td style={{ 
                            padding: '6px 8px', 
                            color: statusColor(well.status).solid,
                            fontWeight: 500
                          }}>
                            {well.name}
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: '#ccc' }}>
                            {well.tvd.toLocaleString()}'
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: '#ccc' }}>
                            {well.td.toLocaleString()}'
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: '#ccc' }}>
                            {well.lateralLength.toLocaleString()}'
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'center', color: '#ccc' }}>
                            <span style={{ color: '#8CC540' }}>{completedStages}</span>
                            <span style={{ color: '#666' }}>/{totalStages}</span>
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%',
                              backgroundColor: statusColor(well.status).solid
                            }}></span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <canvas
                ref={canvasRef}
                style={{ width: '100%', height: '100%', display: 'block', cursor: 'grab' }}
              />
              
              {/* 3D Stage Hover Tooltip */}
              {hovered3DStage && (
                <div style={{
                  position: 'absolute',
                  left: hovered3DStage.x + 16,
                  top: hovered3DStage.y - 40,
                  zIndex: 20,
                  backgroundColor: 'rgba(48, 51, 53, 0.95)',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  borderLeft: `3px solid ${statusColor(hovered3DStage.status).solid}`,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap'
                }}>
                  <div style={{ 
                    color: statusColor(hovered3DStage.status).solid, 
                    fontWeight: 600, fontSize: '13px', marginBottom: '4px'
                  }}>
                    {hovered3DStage.wellName} - Stage {hovered3DStage.stageNumber}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px' }}>
                    <span style={{
                      display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%',
                      backgroundColor: statusColor(hovered3DStage.status).solid
                    }}></span>
                    <span style={{ color: '#aaa', textTransform: 'capitalize' }}>{hovered3DStage.status.toLowerCase()}</span>
                    <span style={{ color: '#666', marginLeft: '8px' }}>Click to view details</span>
                  </div>
                </div>
              )}
            </div>
          ) : selectedWell ? (
            // Stage Grid
            <div style={{ flex: 1, padding: '16px', overflowY: 'auto' }}>
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ margin: '0 0 4px 0', color: '#fff', fontSize: '18px' }}>{selectedWell.name}</h2>
                  <div style={{ color: '#aaa', fontSize: '13px' }}>
                    {selectedWell.padName} | API: {selectedWell.api} | Lateral: {selectedWell.lateralLength?.toLocaleString()} ft
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedWell(null)}
                  style={{
                    backgroundColor: '#1a1d1f', border: '1px solid #5C646B',
                    color: '#aaa', padding: '8px 16px', borderRadius: '6px',
                    cursor: 'pointer', fontSize: '13px'
                  }}
                >
                  ← Back to 3D View
                </button>
              </div>

              {/* Lateral visualization */}
              <div style={{ 
                backgroundColor: '#303335', borderRadius: '8px', 
                padding: '16px', marginBottom: '16px', border: '1px solid #5C646B'
              }}>
                <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '8px' }}>Lateral View - Click stage for details</div>
                <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
                  <div style={{ color: '#666', fontSize: '11px', marginRight: '8px', fontWeight: 500 }}>TOE</div>
                  {stages.map(stage => (
                    <div 
                      key={stage.id}
                      onClick={() => setSelectedStage(stage)}
                      style={{
                        flex: 1, minWidth: '12px', height: '40px',
                        backgroundColor: stage.status === 'PLANNED' ? '#444' : statusColor(stage.status).solid,
                        borderRadius: '3px', cursor: 'pointer',
                        opacity: selectedStage?.id === stage.id ? 1 : 0.75,
                        transition: 'all 0.2s ease',
                        border: selectedStage?.id === stage.id ? '2px solid #fff' : '1px solid rgba(255,255,255,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '10px', fontWeight: 500,
                        color: selectedStage?.id === stage.id ? '#fff' : (stage.status === 'ACTIVE' ? '#fff' : 'rgba(255,255,255,0.6)')
                      }}
                      title={`Stage ${stage.stageNumber}`}
                    >
                      {stage.stageNumber}
                    </div>
                  ))}
                  <div style={{ color: '#666', fontSize: '11px', marginLeft: '8px', fontWeight: 500 }}>HEEL</div>
                </div>
              </div>

              {/* Stage cards grid */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', 
                gap: '12px' 
              }}>
                {stages.map(stage => (
                  <CompletionCard key={stage.id} stage={stage} />
                ))}
              </div>
            </div>
          ) : (
            // Empty state
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#666' }}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
              <div style={{ marginTop: '16px', fontSize: '16px' }}>Select a pad to view well completions</div>
              <div style={{ marginTop: '8px', fontSize: '13px', color: '#555' }}>Choose from the list on the left</div>
            </div>
          )}
        </div>
      </div>

      {/* Stage Details Sidebar */}
      {/* {selectedStage && (
        <StageDetailsSidebar
          selectedStage={selectedStage}
          selectedWell={selectedWell}
          selectedStageWell={selectedStageWell}
          treatmentData={treatmentData}
          seismosData={seismosData}
          onClose={() => setSelectedStage(null)}
          onViewTreatment={() => setShowTreatmentModal(true)}
        />
      )} */}

      {/* Treatment Modal */}
      <TreatmentModal
        show={showTreatmentModal}
        onHide={() => setShowTreatmentModal(false)}
        stageId={selectedStage ? parseInt(selectedStage.id) : null}
        wellId={selectedWell?.id || selectedStageWell?.id || null}
        stageNumber={selectedStage?.stageNumber || null}
        isRealTimeStreaming={false}
        currentStep={null}
        useFakeData={false}
      />
    </section>
  );
};

export default CompletionsTab;